import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GatheringCategory {
  id: string;
  slug: string;
  name_ar: string;
  name_en: string;
  description_ar: string | null;
  description_en: string | null;
  icon: string;
  gradient_class: string;
  audience: string;
  cover_image_url: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface GatheringPackage {
  id: string;
  category_id: string;
  title_ar: string;
  title_en: string;
  description_ar: string | null;
  description_en: string | null;
  price_kwd: number;
  sessions_count: number;
  weekday: number;
  frequency: string;
  duration_minutes: number;
  max_attendees: number;
  installments_allowed: boolean;
  installment_months: number;
  sort_order: number;
  is_active: boolean;
}

export function useGatheringCategories(onlyActive = true) {
  return useQuery({
    queryKey: ['gathering_categories', onlyActive],
    queryFn: async () => {
      let q = supabase.from('gathering_categories').select('*').order('sort_order');
      if (onlyActive) q = q.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as GatheringCategory[];
    },
  });
}

export function useGatheringCategoryBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['gathering_category', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('gathering_categories')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return data as GatheringCategory | null;
    },
    enabled: !!slug,
  });
}

export function useGatheringPackages(categoryId: string | undefined, onlyActive = true) {
  return useQuery({
    queryKey: ['gathering_packages', categoryId, onlyActive],
    queryFn: async () => {
      if (!categoryId) return [];
      let q = supabase.from('gathering_packages').select('*').eq('category_id', categoryId).order('price_kwd');
      if (onlyActive) q = q.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as GatheringPackage[];
    },
    enabled: !!categoryId,
  });
}

export interface PackageInstructorRow {
  id: string;
  package_id: string;
  instructor_id: string;
  activity_id: string | null;
  sort_order: number;
  profile?: {
    user_id: string;
    full_name: string | null;
    display_name: string | null;
    avatar_url: string | null;
    professional_title: string | null;
  };
  activity?: { id: string; title_ar: string | null; title_en: string | null; price_kwd: number | null } | null;
}

export function usePackageInstructors(packageId: string | undefined) {
  return useQuery({
    queryKey: ['gathering_package_instructors', packageId],
    queryFn: async () => {
      if (!packageId) return [];
      const { data: rows, error } = await supabase
        .from('gathering_package_instructors')
        .select('id, package_id, instructor_id, activity_id, sort_order')
        .eq('package_id', packageId)
        .order('sort_order');
      if (error) throw error;
      const list = (rows ?? []) as PackageInstructorRow[];
      const instructorIds = Array.from(new Set(list.map((r) => r.instructor_id)));
      const activityIds = Array.from(new Set(list.map((r) => r.activity_id).filter(Boolean) as string[]));
      const [{ data: profiles }, { data: activities }] = await Promise.all([
        instructorIds.length
          ? supabase
              .from('profiles')
              .select('user_id, full_name, display_name, avatar_url, professional_title')
              .in('user_id', instructorIds)
          : Promise.resolve({ data: [] as any[] }),
        activityIds.length
          ? supabase.from('activities').select('id, title_ar, title_en, price_kwd').in('id', activityIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const profileMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));
      const activityMap = new Map((activities ?? []).map((a: any) => [a.id, a]));
      return list.map((r) => ({
        ...r,
        profile: profileMap.get(r.instructor_id),
        activity: r.activity_id ? activityMap.get(r.activity_id) : null,
      }));
    },
    enabled: !!packageId,
  });
}

/** Returns map of packageId -> per-person total and avg subscription discount. */
export function usePackagesPricing(packageIds: string[]) {
  const key = [...packageIds].sort().join(',');
  return useQuery({
    queryKey: ['gathering_packages_pricing_v2', key],
    queryFn: async () => {
      const result: Record<string, { perPerson: number; avgDiscount: number; activities: { id: string; title_ar: string | null; price_kwd: number; gallery_urls: string[] | null }[] }> = {};
      if (packageIds.length === 0) return result;
      const { data: links, error } = await supabase
        .from('gathering_package_instructors')
        .select('package_id, activity_id, sort_order')
        .in('package_id', packageIds)
        .order('sort_order');
      if (error) throw error;
      const { data: pkgs } = await supabase
        .from('gathering_packages')
        .select('id, sessions_count, frequency')
        .in('id', packageIds);
      const pkgMap = new Map((pkgs ?? []).map((p: any) => [p.id, p]));
      const activityIds = Array.from(new Set((links ?? []).map((l: any) => l.activity_id).filter(Boolean)));
      const { data: acts } = activityIds.length
        ? await supabase.from('activities').select('id, title_ar, price_kwd, subscription_plans, gallery_urls').in('id', activityIds)
        : { data: [] as any[] };
      const actMap = new Map((acts ?? []).map((a: any) => [a.id, a]));
      const grouped: Record<string, any[]> = {};
      for (const l of links ?? []) {
        if (!l.activity_id) continue;
        const arr = (grouped[l.package_id] ||= []);
        if (!arr.find((x) => x.id === l.activity_id)) {
          const a = actMap.get(l.activity_id);
          if (a) arr.push(a);
        }
      }
      const { data: settings } = await supabase
        .from('home_page_settings')
        .select('subscription_discount_weekly_pct, subscription_discount_monthly_pct, subscription_discount_yearly_pct')
        .limit(1)
        .maybeSingle();
      const adminPct = (period: string) => {
        if (period === 'monthly') return Number((settings as any)?.subscription_discount_monthly_pct ?? 12);
        if (period === 'yearly') return Number((settings as any)?.subscription_discount_yearly_pct ?? 20);
        return Number((settings as any)?.subscription_discount_weekly_pct ?? 5);
      };
      for (const pid of packageIds) {
        const pkg = pkgMap.get(pid);
        const n = pkg?.sessions_count ?? 0;
        const period = pkg?.frequency === 'monthly' ? 'monthly' : pkg?.frequency === 'yearly' ? 'yearly' : 'weekly';
        const picks = (grouped[pid] ?? []).slice(0, n);
        const total = picks.reduce((s, a) => s + Number(a.price_kwd || 0), 0);
        const avgDiscount = adminPct(period);
        result[pid] = { perPerson: total, avgDiscount, activities: picks };
      }
      return result;
    },
    enabled: packageIds.length > 0,
  });
}

export function useMyGatheringSubscriptions() {
  return useQuery({
    queryKey: ['my_gathering_subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gathering_subscriptions')
        .select('*, package:gathering_packages(*, category:gathering_categories(*))')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useGatheringSubscriptionSessions(subscriptionId: string | undefined) {
  return useQuery({
    queryKey: ['gathering_subscription_sessions', subscriptionId],
    queryFn: async () => {
      if (!subscriptionId) return [];
      const { data: sessions, error } = await supabase
        .from('gathering_subscription_sessions')
        .select('*')
        .eq('subscription_id', subscriptionId)
        .order('session_index');
      if (error) throw error;
      const instructorIds = Array.from(
        new Set((sessions ?? []).map((s: any) => s.instructor_id).filter(Boolean)),
      );
      const activityIds = Array.from(
        new Set((sessions ?? []).map((s: any) => s.activity_id).filter(Boolean)),
      );
      const [{ data: profiles }, { data: activities }] = await Promise.all([
        instructorIds.length
          ? supabase
              .from('profiles')
              .select('user_id, full_name, display_name, avatar_url')
              .in('user_id', instructorIds)
          : Promise.resolve({ data: [] as any[] }),
        activityIds.length
          ? supabase.from('activities').select('id, title_ar, title_en').in('id', activityIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const pm = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));
      const am = new Map((activities ?? []).map((a: any) => [a.id, a]));
      return (sessions ?? []).map((s: any) => ({
        ...s,
        profile: s.instructor_id ? pm.get(s.instructor_id) : null,
        activity: s.activity_id ? am.get(s.activity_id) : null,
      }));
    },
    enabled: !!subscriptionId,
  });
}
